/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatTabChangeEvent, MatTabsModule } from "@angular/material/tabs";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { UserInfo } from "../../shared/models/http/user/user-info.model";
import { AuthorizationService } from "../../shared/services/authorization.service";
import { UserStateService } from "../../shared/services/state/user-state.service";
import { COMMON_TABS, PATHS } from "../../shared/utils/constants";

type DashboardTab = {
	label: string;
	routes: string[];
};

@Component({
	selector: "globalictcc-dashboard",
	standalone: true,
	imports: [RouterOutlet, MatTabsModule],
	templateUrl: "./dashboard.component.html",
	styleUrl: "./dashboard.component.scss"
})
export class DashboardComponent implements OnInit, OnDestroy {
	public selectedTabIndex: number = 0;
	public tabs: DashboardTab[] = [];

	private _destroy$ = new Subject<void>();

	constructor(
		private _router: Router,
		private _userStateService: UserStateService,
		private _authorizationService: AuthorizationService
	) {}

	ngOnInit() {
		this._userStateService.userInfo$.pipe(takeUntil(this._destroy$)).subscribe({
			next: userInfo => {
				this.tabs = this._buildTabs(userInfo);
				this.selectedTabIndex = this._getTabIndex();
			}
		});

		this._router.events.pipe(takeUntil(this._destroy$)).subscribe({
			next: event => {
				if (event instanceof NavigationEnd) this.selectedTabIndex = this._getTabIndex();
			}
		});
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Retrieves the index of the tab that contains the current dashboard page.
	 *
	 * @returns The tab index for the current route, or `0` when no tab matches.
	 */
	private _getTabIndex(): number {
		const urlSegment = this._router.parseUrl(this._router.url).root.children["primary"]
			?.segments[1]?.path;
		const index = this.tabs.findIndex(tab => !!urlSegment && tab.routes.includes(urlSegment));
		return index >= 0 ? index : 0;
	}

	/**
	 * Handles the tab change event and navigates to the corresponding route.
	 *
	 * @param event - The tab change event containing the selected tab information.
	 */
	public handleTabChanged(event: MatTabChangeEvent) {
		const targetRoute = this.tabs[event.index]?.routes[0];
		if (!targetRoute) return;
		this._router.navigateByUrl(`${PATHS.DASHBOARD}/${targetRoute}`);
	}

	private _buildTabs(userInfo: UserInfo): DashboardTab[] {
		return COMMON_TABS.filter(tab => !tab.requiresDcEnabled || userInfo.dcEnabled)
			.map(tab => ({
				label: tab.label,
				routes: tab.pages
					.filter(page => this._authorizationService.hasGrant(`${page.pageName}:tab`))
					.map(page => page.route)
			}))
			.filter(tab => tab.routes.length > 0);
	}
}
